import { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { ExternalLink, ChevronDown } from "lucide-react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "@/packages/utils";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { api } from "convex/_generated/api";
import type { Framework } from "./dashboard.types";

import { getPaletteColor } from "@/lib/color-palettes";

function getFrameworkColor(index: number): string {
  return getPaletteColor("framework", (index % 8) + 1);
}

function getStatus(cov: number): {
  labelKey:
    | "frameworkCoverage.statusOnTrack"
    | "frameworkCoverage.statusAtRisk"
    | "frameworkCoverage.statusBehind";
  className: string;
} {
  if (cov >= 80)
    return {
      labelKey: "frameworkCoverage.statusOnTrack",
      className: "text-emerald-600 bg-emerald-50",
    };
  if (cov >= 50)
    return {
      labelKey: "frameworkCoverage.statusAtRisk",
      className: "text-amber-600 bg-amber-50",
    };
  return {
    labelKey: "frameworkCoverage.statusBehind",
    className: "text-red-600 bg-red-50",
  };
}

function ProgressBar({
  cov,
  color,
  index,
}: {
  cov: number;
  color: string;
  index: number;
}) {
  return (
    <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden">
      <motion.div
        className="h-full rounded-full"
        style={{ backgroundColor: color }}
        initial={{ width: 0 }}
        animate={{ width: `${cov}%` }}
        transition={{ ...springs.smooth, delay: index * 0.06 }}
      />
    </div>
  );
}

/** Detail metrics grid — reused in both expanded and always-visible modes */
function DetailMetrics({ fw }: { fw: Framework }) {
  const { t } = useTranslation(["dashboard"]);
  const { workspaceSlug } = useParams({ strict: false });
  const status = getStatus(fw.cov);
  const tags = fw.tags;

  return (
    <div className="space-y-2.5">
      {/* Metrics row */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <span className="text-[11px] text-muted">
            {t("frameworkCoverage.metricsControls")}
          </span>
          <p className="text-sm font-medium text-primary">
            {fw.met}/{fw.ctrl}
          </p>
        </div>
        <div>
          <span className="text-[11px] text-muted">
            {t("frameworkCoverage.metricsDocs")}
          </span>
          <p className="text-sm font-medium text-primary">{fw.docCov}%</p>
        </div>
        <div>
          <span className="text-[11px] text-muted">
            {t("frameworkCoverage.metricsOpenProcesses")}
          </span>
          <p className="text-sm font-medium text-primary">{fw.openProcesses}</p>
        </div>
        <div>
          <span className="text-[11px] text-muted">
            {t("frameworkCoverage.metricsDue")}
          </span>
          <p className="text-sm font-medium text-primary">{fw.due}</p>
        </div>
      </div>

      {/* Status + trigger */}
      <div className="flex items-center gap-2">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.className}`}
        >
          {t(status.labelKey)}
        </span>
        {fw.trigger && (
          <span className="text-[11px] text-muted">{fw.trigger}</span>
        )}
      </div>

      {/* Tags + link */}
      <div className="flex items-end justify-between mt-1">
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag.name}
                className="inline-flex items-center gap-1 text-[11px] text-secondary px-2 py-0.5 rounded-full bg-surface border border-border/60"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
              </span>
            ))}
          </div>
        ) : (
          <div />
        )}
        {fw.auditSlug && workspaceSlug && (
          <Link
            to="/$workspaceSlug/audits/$auditId"
            params={{
              workspaceSlug: workspaceSlug as string,
              auditId: fw.auditSlug,
            }}
            className="text-accent hover:text-accent/80 transition-colors shrink-0 ml-2"
            title={t("frameworkCoverage.openAudit")}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

/** Expanded details shown on click (for 3+ audits collapsible mode) */
function ExpandedDetail({ fw }: { fw: Framework }) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={springs.snappy}
      className="overflow-hidden"
    >
      <div className="mt-3 pt-3 border-t border-border/40">
        <DetailMetrics fw={fw} />
      </div>
    </motion.div>
  );
}

/** Solo view for 1 audit — no card wrapper, always shows full details */
function SoloFrameworkView({ fw }: { fw: Framework }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.snappy}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-medium text-primary">{fw.name}</span>
        <span className="text-sm font-bold text-primary tabular-nums">
          {fw.cov}%
        </span>
      </div>
      <ProgressBar cov={fw.cov} color={fw.color} index={0} />
      <div className="mt-3 pt-3 border-t border-border/40">
        <DetailMetrics fw={fw} />
      </div>
    </motion.div>
  );
}

/** Single framework card — click to expand */
function FrameworkCard({
  fw,
  index,
  expanded,
  onToggle,
}: {
  fw: Framework;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.snappy, delay: index * 0.05 }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left rounded-2xl bg-surface px-5 py-3.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
      >
        {/* Header: title + percentage */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-medium text-primary">
            {fw.name}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-primary tabular-nums">
              {fw.cov}%
            </span>
            <motion.div
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={14} className="text-muted" />
            </motion.div>
          </div>
        </div>

        {/* Progress bar */}
        <ProgressBar cov={fw.cov} color={fw.color} index={index} />

        {/* Expanded details */}
        <AnimatePresence>
          {expanded && <ExpandedDetail fw={fw} />}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}

function FrameworkGhost() {
  const ghostItems = [
    { width: "60%", barColor: "#94a3b8" },
    { width: "35%", barColor: "#94a3b8" },
    { width: "80%", barColor: "#94a3b8" },
  ];

  return (
    <div className="space-y-2 opacity-15">
      {ghostItems.map((item, i) => (
        <div
          key={`ghost-fw-${i.toString()}`}
          className="rounded-2xl bg-surface px-5 py-3.5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-28 h-3.5 bg-card-hover rounded" />
            <div className="w-10 h-3.5 bg-card-hover rounded" />
          </div>
          <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                backgroundColor: item.barColor,
                width: item.width,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonFramework() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-2xl bg-surface px-5 py-3.5 shadow-sm animate-pulse"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-28 h-3.5 bg-card-hover rounded" />
            <div className="w-10 h-3.5 bg-card-hover rounded" />
          </div>
          <div className="h-1.5 bg-card-hover rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function FrameworkCoverage() {
  const { t } = useTranslation(["dashboard"]);
  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const [expandedSet, setExpandedSet] = useState<Set<number>>(() => new Set());
  const initializedRef = useRef(false);

  const { data: frameworksData, isLoading } = useWorkspaceQuery(
    api.dashboard.queries.frameworkCoverage.get,
    {},
  );

  const frameworks: Framework[] = (frameworksData ?? []).map((fw, i) => ({
    name: fw.templateName,
    cov: fw.coverage,
    ctrl: fw.totalItems,
    met: fw.approvedItems,
    color: getFrameworkColor(i),
    docCov: typeof fw.docCov === "number" ? fw.docCov : 0,
    openProcesses: typeof fw.openProcesses === "number" ? fw.openProcesses : 0,
    due: typeof fw.due === "string" ? fw.due : "—",
    trigger: typeof fw.trigger === "string" ? fw.trigger : undefined,
    auditSlug: fw.auditId,
    tags: Array.isArray(fw.tags) ? fw.tags : [],
  }));

  const count = frameworks.length;

  // Set default expanded state when frameworks first load
  if (count > 0 && !initializedRef.current) {
    initializedRef.current = true;
    if (count === 3) {
      setExpandedSet(new Set([0]));
    }
  }

  // Strict accordion for 2 audits: always exactly one open
  const toggleAccordion = useCallback((index: number) => {
    setExpandedIndex((prev) =>
      prev === index ? (index === 0 ? 1 : 0) : index,
    );
  }, []);

  // Independent toggle for 3+ audits
  const toggleExpand = useCallback((index: number) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="flex items-center px-4 pt-3 pb-3 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {count <= 1
            ? t("dashboard:frameworkCoverage.titleSingular")
            : t("dashboard:frameworkCoverage.titlePlural")}
        </h3>
      </div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="px-4 pb-3"
          >
            <SkeletonFramework />
          </motion.div>
        ) : count === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex items-center justify-center px-4 pb-3"
          >
            <FrameworkGhost />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="framework-body overflow-y-auto flex-1 min-h-0 px-4 pb-3 flex flex-col"
          >
            {count === 1 ? (
              <SoloFrameworkView fw={frameworks[0]} />
            ) : (
              <div className="space-y-2">
                {frameworks.map((fw, i) =>
                  count === 2 ? (
                    <FrameworkCard
                      key={fw.name}
                      fw={fw}
                      index={i}
                      expanded={expandedIndex === i}
                      onToggle={() => toggleAccordion(i)}
                    />
                  ) : (
                    <FrameworkCard
                      key={fw.name}
                      fw={fw}
                      index={i}
                      expanded={expandedSet.has(i)}
                      onToggle={() => toggleExpand(i)}
                    />
                  ),
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
